import React, { useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/common/Navbar";
import "../../styles/profilePage/ProfileEditPage.css";
import config from "../../config/config";

const EditProfile = () => {
  const [user, setUser] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    callSign: "",
  });
  const [editError, setEditError] = useState("");
  const navigate = useNavigate();

  // Scroll to top on component render
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch user data from backend and update state
  const fetchUserData = async (userId) => {
    try {
      const response = await fetch(
        `${config.API_URL}/api/user/user-profile/${userId}`
      );
      const data = await response.json();

      if (response.ok) {
        setUser((prevUser) => ({
          ...prevUser,
          username: data.username || prevUser.username,
          // email: data.email || prevUser.email, //? Removed for Data Security
          callSign: data.call_sign, // Correct mapping here
        }));
      } else {
        console.error("Failed to fetch user data:", data.message);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/"); // Redirect to home if no token
      return;
    }

    try {
      const decoded = jwtDecode(token);
      setUser({
        username: decoded.username || "",
        // email: decoded.email || "", //? Removed for Data Security
        callSign: decoded.callSign || "",
        password: "",
        confirmPassword: "",
      });
      // Fetch additional user data from the backend
      fetchUserData(decoded.id);
    } catch (error) {
      console.error("Invalid token:", error);
      navigate("/");
    }
  }, [navigate]);

  const handleChange = (e) => {
    const value = e.target.value || ""; // Ensure the value is not null or undefined
    setUser({ ...user, [e.target.name]: value });
    setEditError(""); // Clear error when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Ensure that fields like username and callSign are not null or undefined
    const trimmedUsername = user.username ? user.username.trim() : "";
    const trimmedCallSign = user.callSign ? user.callSign.trim() : "";

    // Validate that passwords match
    if (user.password !== user.confirmPassword) {
      setEditError("Passwords do not match.");
      return;
    }

    // Validate password strength
    const passwordPattern =
      /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])[A-Za-z\d!@#$%^&*(),.?":{}|<>]{8,}$/;
    if (user.password && !passwordPattern.test(user.password)) {
      setEditError(
        "Password must be at least 8 characters, include a number, one special character, and one uppercase letter."
      );
      return;
    }

    //? Removed for Data Security
    // Validate email format
    // const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    // if (!emailPattern.test(user.email)) {
    //   setEditError("Please enter a valid email address.");
    //   return;
    // }

    // Check if username is empty
    if (trimmedUsername === "") {
      setEditError("Username cannot be empty.");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
      return;
    }

    const decoded = jwtDecode(token);

    try {
      const response = await fetch(
        `${config.API_URL}/api/user/update-profile`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userId: decoded.id,
            username: trimmedUsername,
            // email: user.email.trim() || null, //? Removed for Data Security
            password: user.password || null,
            confirmPassword: user.confirmPassword || null,
            call_sign: trimmedCallSign || "", // Ensure this is sent correctly
          }),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        setEditError(data.message);
        return;
      }

      setUser((prevUser) => ({
        ...prevUser,
        username: trimmedUsername,
        // email: user.email.trim() || prevUser.email, //? Removed for Data Security
        callSign: trimmedCallSign || prevUser.callSign,
        password: "",
        confirmPassword: "",
      }));

      navigate("/profile");
    } catch (error) {
      console.error("Profile update error:", error);
      setEditError("Something went wrong. Please try again.");
    }
  };

  return (
    <div>
      <Navbar />
      <div className="container-fluid d-flex justify-content-center align-items-center mt-5">
        <div className="d-flex flex-column align-items-center profileEdit">
          {/* Profile Picture Circle with Image */}
          <div className="circle">
            <img
              src="/profilepage-placeholder.jpg"
              alt="Profile"
              className="profileImage"
            />
          </div>

          {/* Edit Profile Form */}
          <form onSubmit={handleSubmit}>
            <div className="form-group mb-3">
              <label className="mb-2">Username</label>
              <input
                type="text"
                name="username"
                className="form-control"
                value={user.username || ""}
                onChange={handleChange}
              />
            </div>

            {/* //? Removed for Data Security */}
            {/* <div className="form-group mb-3">
              <label className="mb-2">Email</label>
              <input
                type="email"
                name="email"
                className="form-control"
                value={user.email || ""}
                onChange={handleChange}
              />
            </div> */}

            <div className="form-group mb-3">
              <label className="mb-2">Password</label>
              <input
                type="password"
                name="password"
                className="form-control"
                value={user.password || ""}
                onChange={handleChange}
              />
            </div>

            <div className="form-group mb-3">
              <label className="mb-2">Confirm Password</label>
              <input
                type="password"
                name="confirmPassword"
                className="form-control"
                value={user.confirmPassword || ""}
                onChange={handleChange}
                required={!!user.password} // Required only if password is filled
              />
            </div>

            <div className="form-group mb-4">
              <label className="mb-2">Call/Sign</label>
              <input
                type="text"
                name="callSign" // Make sure this matches your SQL column
                className="form-control"
                value={user.callSign}
                onChange={handleChange}
              />
            </div>

            {editError && <p className="formError">{editError}</p>}

            <div className="profileEditButtons mt-3 d-flex flex-column align-items-center">
              {/* Save Changes Button */}
              <button type="submit" className="save mb-3">
                Save Changes
              </button>

              {/* Back Button */}
              <button
                type="button"
                className="cancel"
                onClick={() => navigate("/profile")}
              >
                Back
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditProfile;
